import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import PhygitalIcon from "@/components/PhygitalIcon";
import {
  Lock,
  ArrowRight,
  Check,
  Users,
  DollarSign,
  Calendar,
  TrendingUp,
  Globe,
  Target,
  Zap,
  Shield,
  ChevronRight,
  ChevronDown,
  ExternalLink,
  Sparkles,
  Building,
  MapPin,
  Clock,
  BarChart3,
  Layers,
  Share,
  Smartphone,
  Award,
  Heart,
  Briefcase,
  Scissors,
  Palette,
  PartyPopper,
  Eye,
  FileText,
  PieChart,
  User,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Rocket,
  Settings,
  CreditCard,
  Package,
  Mail,
  Phone
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Password for access
const PLAN_PASSWORD = "phygital_admin_2026";

// Navigation sections
const SECTIONS = [
  { id: "executive", label: "Executive Summary" },
  { id: "problem", label: "Problem" },
  { id: "solution", label: "Solution" },
  { id: "market", label: "Market" },
  { id: "business-model", label: "Business Model" },
  { id: "gtm", label: "Go-to-Market" },
  { id: "traction", label: "Traction" },
  { id: "roadmap", label: "Roadmap" },
  { id: "financials", label: "Financials" },
  { id: "team", label: "Team" },
  { id: "appendix", label: "Appendix" }
];

const BusinessPlanPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [activeSection, setActiveSection] = useState("executive");
  const [expandedAppendix, setExpandedAppendix] = useState(null);
  const sectionRefs = useRef({});

  // Check session on mount
  useEffect(() => {
    const auth = sessionStorage.getItem("bizplan_auth");
    if (auth === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === PLAN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem("bizplan_auth", "true");
      setError("");
    } else {
      setError("Incorrect password");
    }
  };

  // Scroll spy
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -70% 0px" }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [isAuthenticated]);

  const scrollToSection = (id) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Password screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <PhygitalIcon className="w-16 h-16 mx-auto mb-4 text-white" />
            <h1 className="text-2xl font-bold text-white mb-2">Business Plan</h1>
            <p className="text-gray-400">Enter password to access</p>
          </div>
          
          <form onSubmit={handleSubmit} className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10">
            <div className="space-y-4">
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                />
              </div>
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <Button type="submit" className="w-full bg-white text-black hover:bg-gray-100">
                Access Plan <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </form>
          
          <div className="mt-6 text-center">
            <Link to="/" className="text-gray-500 hover:text-white text-sm">
              ← Back to Homepage
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2">
              <PhygitalIcon className="w-6 h-6" />
              <span className="font-semibold text-sm">Business Plan</span>
            </Link>
            
            <div className="flex items-center gap-1 overflow-x-auto">
              {SECTIONS.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-all ${
                    activeSection === section.id
                      ? "bg-black text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {section.label}
                </button>
              ))}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                sessionStorage.removeItem("bizplan_auth");
                setIsAuthenticated(false);
              }}
              className="text-xs"
            >
              Exit
            </Button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        
        {/* Executive Summary */}
        <section id="executive" ref={(el) => (sectionRefs.current.executive = el)} className="mb-16">
          <div className="border-l-4 border-black pl-6 mb-8">
            <h1 className="text-3xl font-bold mb-2">Executive Summary</h1>
            <p className="text-gray-600">What is Phygital? In simple terms.</p>
          </div>
          
          <div className="prose prose-gray max-w-none">
            {/* Simple One-Liner */}
            <div className="bg-black text-white rounded-xl p-6 mb-8">
              <p className="text-xl font-medium text-center">
                "Phygital is the <span className="text-emerald-400">preference layer</span> for beauty & lifestyle services - 
                tap your phone to share your style preferences with any provider, instantly."
              </p>
            </div>
            
            {/* Who We Serve */}
            <div className="grid md:grid-cols-2 gap-6 my-8">
              <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-100">
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-600" />
                  For Consumers (Free Forever)
                </h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-600 mt-0.5" />
                    <span>Save your "Defaults" - hair color, skin type, preferences</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-600 mt-0.5" />
                    <span>Collect "Inspo" - screenshot looks you want</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-600 mt-0.5" />
                    <span>Tap to share with any stylist/MUA/provider</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-green-600" />
                  For Providers ($29-149/mo)
                </h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-600 mt-0.5" />
                    <span>See client's full context before appointment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-600 mt-0.5" />
                    <span>Manage bookings, payments, portfolio</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-600 mt-0.5" />
                    <span>Build your digital storefront</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Simple Business Model */}
            <div className="bg-gray-50 rounded-xl p-6 mb-8">
              <h3 className="font-bold text-lg mb-4">The Business Model (Simple)</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-white rounded-lg">
                  <p className="text-3xl font-bold text-emerald-600">Free</p>
                  <p className="text-sm text-gray-600">Consumers</p>
                  <p className="text-xs text-gray-400 mt-1">Build the network</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg">
                  <p className="text-3xl font-bold text-green-600">$29-149</p>
                  <p className="text-sm text-gray-600">Providers/mo</p>
                  <p className="text-xs text-gray-400 mt-1">SaaS subscription</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg">
                  <p className="text-3xl font-bold text-blue-600">3%</p>
                  <p className="text-sm text-gray-600">Transaction fee</p>
                  <p className="text-xs text-gray-400 mt-1">On bookings</p>
                </div>
              </div>
            </div>
            
            {/* Current Status */}
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-xl p-6">
              <h3 className="font-semibold mb-3">Where We Are Now</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">Completed</p>
                  <ul className="space-y-1">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      Platform MVP built
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      First event hosted (HTHF LAFW)
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      Pilot program launched
                    </li>
                  </ul>
                </div>
                <div>
                  <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">Next Steps</p>
                  <ul className="space-y-1">
                    <li className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-yellow-400" />
                      MUA pilot partners (in progress)
                    </li>
                    <li className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-yellow-400" />
                      Acuity integration live
                    </li>
                    <li className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-yellow-400" />
                      Consumer app launch Q2
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Problem */}
        <section id="problem" ref={(el) => (sectionRefs.current.problem = el)} className="mb-16">
          <div className="border-l-4 border-red-500 pl-6 mb-8">
            <h2 className="text-2xl font-bold mb-2">The Problem</h2>
            <p className="text-gray-600">A $63B industry running on fragmented communication</p>
          </div>
          
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  <h3 className="font-semibold">For Consumers</h3>
                </div>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Repeat preferences at every appointment</li>
                  <li>• Screenshots scattered across apps</li>
                  <li>• No way to share context efficiently</li>
                  <li>• Miscommunication leads to bad experiences</li>
                </ul>
              </div>
              <div className="border border-gray-200 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  <h3 className="font-semibold">For Providers</h3>
                </div>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Manual client intake for every new client</li>
                  <li>• Juggling multiple scheduling tools</li>
                  <li>• No persistent client preference history</li>
                  <li>• Lost revenue from no-shows and miscommunication</li>
                </ul>
              </div>
            </div>
            
            <div className="bg-red-50 border border-red-100 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <DollarSign className="w-6 h-6 text-red-600" />
                <span className="text-2xl font-bold text-red-600">$63B</span>
              </div>
              <p className="text-gray-700">
                The US beauty services market alone is valued at $63 billion, yet the industry still relies on 
                fragmented tools, text messages, and verbal communication to understand client preferences.
              </p>
            </div>
          </div>
        </section>

        {/* Solution */}
        <section id="solution" ref={(el) => (sectionRefs.current.solution = el)} className="mb-16">
          <div className="border-l-4 border-green-500 pl-6 mb-8">
            <h2 className="text-2xl font-bold mb-2">The Solution</h2>
            <p className="text-gray-600">Save → Share → Sync</p>
          </div>
          
          <div className="space-y-8">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-bold mb-2">1. SAVE</h3>
                <p className="text-sm text-gray-600">
                  Consumers save their preferences (Defaults) and inspiration photos (Inspo) in their personal wallet.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Share className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="font-bold mb-2">2. SHARE</h3>
                <p className="text-sm text-gray-600">
                  One tap (NFC) or scan (QR) shares their complete preference wallet with any provider.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="font-bold mb-2">3. SYNC</h3>
                <p className="text-sm text-gray-600">
                  Providers receive full context instantly - preferences, inspo, notes - no intake forms needed.
                </p>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold mb-4">Key Features</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { icon: Palette, title: "Defaults", desc: "Hair, makeup, nails, events preferences" },
                  { icon: Eye, title: "Inspo", desc: "Save and organize inspiration photos" },
                  { icon: Calendar, title: "Experiences", desc: "Plan appointments by occasion" },
                  { icon: Smartphone, title: "NFC Sharing", desc: "Tap to share full context" },
                  { icon: BarChart3, title: "Provider Dashboard", desc: "Client management & analytics" },
                  { icon: CreditCard, title: "Payments", desc: "Integrated invoicing & deposits" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <item.icon className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-sm">{item.title}</h4>
                      <p className="text-xs text-gray-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Market */}
        <section id="market" ref={(el) => (sectionRefs.current.market = el)} className="mb-16">
          <div className="border-l-4 border-blue-500 pl-6 mb-8">
            <h2 className="text-2xl font-bold mb-2">Market Opportunity</h2>
            <p className="text-gray-600">Large TAM with strong unit economics</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-blue-50 rounded-xl p-6 text-center">
              <p className="text-3xl font-bold text-blue-600">$63B</p>
              <p className="text-sm text-gray-600 mt-1">US Beauty Services TAM</p>
            </div>
            <div className="bg-emerald-50 rounded-xl p-6 text-center">
              <p className="text-3xl font-bold text-emerald-600">1.2M+</p>
              <p className="text-sm text-gray-600 mt-1">US Beauty Professionals</p>
            </div>
            <div className="bg-green-50 rounded-xl p-6 text-center">
              <p className="text-3xl font-bold text-green-600">$789B</p>
              <p className="text-sm text-gray-600 mt-1">Global Beauty Market</p>
            </div>
          </div>
          
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-4 font-semibold">Category</th>
                  <th className="text-right p-4 font-semibold">Market Size</th>
                  <th className="text-right p-4 font-semibold">Providers</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr><td className="p-4">Hair Salons</td><td className="p-4 text-right">$48B</td><td className="p-4 text-right">~990K</td></tr>
                <tr><td className="p-4">Nail Salons</td><td className="p-4 text-right">$8.5B</td><td className="p-4 text-right">~130K</td></tr>
                <tr><td className="p-4">Makeup Artists</td><td className="p-4 text-right">$2B</td><td className="p-4 text-right">~50K</td></tr>
                <tr><td className="p-4">Estheticians</td><td className="p-4 text-right">$4.5B</td><td className="p-4 text-right">~80K</td></tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Business Model */}
        <section id="business-model" ref={(el) => (sectionRefs.current["business-model"] = el)} className="mb-16">
          <div className="border-l-4 border-amber-500 pl-6 mb-8">
            <h2 className="text-2xl font-bold mb-2">Business Model</h2>
            <p className="text-gray-600">B2B SaaS for providers, free for consumers</p>
          </div>
          
          <div className="space-y-8">
            <div className="bg-gray-900 text-white rounded-xl p-6">
              <h3 className="font-semibold mb-4">Provider Subscription Tiers</h3>
              <div className="grid md:grid-cols-4 gap-4">
                {[
                  { tier: "Free", price: "$0", features: ["5 clients", "Basic portfolio", "QR sharing"] },
                  { tier: "Solo", price: "$11/mo", features: ["Unlimited clients", "Analytics", "Payments", "Custom branding"] },
                  { tier: "Team", price: "$44/mo", features: ["5 team members", "Shared directory", "Team analytics"] },
                  { tier: "Luxe", price: "$99/mo", features: ["NFC reader included", "Custom branding", "Dedicated manager", "Priority support"] }
                ].map((plan, idx) => (
                  <div key={idx} className={`p-4 rounded-xl ${plan.tier === "Luxe" ? "bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30" : "bg-white/5"}`}>
                    <h4 className="font-bold text-lg">{plan.tier}</h4>
                    <p className="text-2xl font-bold mt-1">{plan.price}</p>
                    <ul className="mt-3 space-y-1 text-xs text-gray-300">
                      {plan.features.map((f, i) => (
                        <li key={i} className="flex items-center gap-1">
                          <Check className="w-3 h-3" /> {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="border border-gray-200 rounded-xl p-5">
                <h3 className="font-semibold mb-3">Revenue Streams</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-green-500" /> Provider subscriptions (primary)</li>
                  <li className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-green-500" /> Payment processing fees</li>
                  <li className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-green-500" /> NFC hardware sales ($150)</li>
                  <li className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-gray-300" /> Premium features (future)</li>
                </ul>
              </div>
              <div className="border border-gray-200 rounded-xl p-5">
                <h3 className="font-semibold mb-3">Unit Economics</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between"><span>Avg Revenue/Provider</span><span className="font-medium">$30/mo</span></li>
                  <li className="flex justify-between"><span>Target CAC</span><span className="font-medium">&lt;$50</span></li>
                  <li className="flex justify-between"><span>Target LTV</span><span className="font-medium">$500+</span></li>
                  <li className="flex justify-between"><span>Gross Margin</span><span className="font-medium">80%+</span></li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Go-to-Market */}
        <section id="gtm" ref={(el) => (sectionRefs.current.gtm = el)} className="mb-16">
          <div className="border-l-4 border-emerald-500 pl-6 mb-8">
            <h2 className="text-2xl font-bold mb-2">Go-to-Market Strategy</h2>
            <p className="text-gray-600">Bottoms-up adoption through consumer utility</p>
          </div>
          
          <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { phase: "Phase 1", title: "Seed", desc: "Pilot providers (MUA, Hair, Events) in LA. Lifetime Luxe for early adopters.", status: "current" },
                { phase: "Phase 2", title: "Grow", desc: "Expand to nail techs, estheticians. Consumer-led provider referrals.", status: "next" },
                { phase: "Phase 3", title: "Scale", desc: "B2B sales team. Salon/studio partnerships. National expansion.", status: "future" }
              ].map((p, idx) => (
                <div key={idx} className={`p-5 rounded-xl border-2 ${p.status === "current" ? "border-emerald-500 bg-emerald-50" : "border-gray-200"}`}>
                  <span className={`text-xs font-bold ${p.status === "current" ? "text-emerald-600" : "text-gray-400"}`}>{p.phase}</span>
                  <h3 className="font-bold mt-1">{p.title}</h3>
                  <p className="text-sm text-gray-600 mt-2">{p.desc}</p>
                </div>
              ))}
            </div>
            
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold mb-4">Acquisition Channels</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Heart className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">Consumer Referrals</h4>
                    <p className="text-gray-500">Consumers share with their providers, driving organic B2B leads</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">Provider Networks</h4>
                    <p className="text-gray-500">Stylists refer other stylists in their network</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <PartyPopper className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">Event Partnerships</h4>
                    <p className="text-gray-500">Beauty expos, trade shows, influencer events</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">Salon Partnerships</h4>
                    <p className="text-gray-500">Onboard entire salons with team plans</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Traction */}
        <section id="traction" ref={(el) => (sectionRefs.current.traction = el)} className="mb-16">
          <div className="border-l-4 border-green-500 pl-6 mb-8">
            <h2 className="text-2xl font-bold mb-2">Current Traction</h2>
            <p className="text-gray-600">Q1 2026 Pilot Status</p>
          </div>
          
          <div className="space-y-6">
            <div className="grid md:grid-cols-4 gap-4">
              <div className="bg-green-50 rounded-xl p-5 text-center">
                <p className="text-3xl font-bold text-green-600">3</p>
                <p className="text-sm text-gray-600">Pilot Providers</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-5 text-center">
                <p className="text-3xl font-bold text-blue-600">22</p>
                <p className="text-sm text-gray-600">Demo Accounts</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-5 text-center">
                <p className="text-3xl font-bold text-emerald-600">3</p>
                <p className="text-sm text-gray-600">Categories Live</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-5 text-center">
                <p className="text-3xl font-bold text-amber-600">LA</p>
                <p className="text-sm text-gray-600">Launch Market</p>
              </div>
            </div>
            
            <div className="border border-gray-200 rounded-xl p-6">
              <h3 className="font-semibold mb-4">Confirmed Pilot Providers</h3>
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  { name: "Shelsea Luxe", category: "MUA", location: "Los Angeles", status: "Confirmed" },
                  { name: "Hair by Amayah", category: "Hair", location: "Los Angeles", status: "Confirmed" },
                  { name: "HTHF Events", category: "Events", location: "Los Angeles", status: "Confirmed" }
                ].map((p, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-gradient-to-br from-gray-800 to-black rounded-full flex items-center justify-center">
                      {p.category === "MUA" && <Palette className="w-5 h-5 text-white" />}
                      {p.category === "Hair" && <Scissors className="w-5 h-5 text-white" />}
                      {p.category === "Events" && <PartyPopper className="w-5 h-5 text-white" />}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-sm">{p.name}</h4>
                      <p className="text-xs text-gray-500">{p.category} • {p.location}</p>
                    </div>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Legacy Luxe</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Roadmap */}
        <section id="roadmap" ref={(el) => (sectionRefs.current.roadmap = el)} className="mb-16">
          <div className="border-l-4 border-indigo-500 pl-6 mb-8">
            <h2 className="text-2xl font-bold mb-2">Product Roadmap</h2>
            <p className="text-gray-600">2026 Development Timeline</p>
          </div>
          
          <div className="space-y-4">
            {[
              { q: "Q1 2026", title: "Foundation", items: ["Consumer profile (Defaults, Inspo, Experiences)", "Provider dashboard MVP", "Phone auth + Google OAuth", "Help center with Notion integration"], status: "complete" },
              { q: "Q2 2026", title: "Integration", items: ["Acuity Scheduling integration", "Stripe payments", "SMS notifications (Sinch)", "NFC hardware integration"], status: "next" },
              { q: "Q3 2026", title: "Scale", items: ["Team plans & permissions", "Advanced analytics", "Mobile app (React Native)", "API for third-party integrations"], status: "future" },
              { q: "Q4 2026", title: "Expand", items: ["Multi-location support", "Franchise features", "Enterprise sales tools", "International expansion prep"], status: "future" }
            ].map((quarter, idx) => (
              <div key={idx} className={`border-l-4 ${quarter.status === "complete" ? "border-green-500" : quarter.status === "next" ? "border-blue-500" : "border-gray-300"} pl-4 py-3`}>
                <div className="flex items-center gap-3 mb-2">
                  <span className={`text-sm font-bold ${quarter.status === "complete" ? "text-green-600" : quarter.status === "next" ? "text-blue-600" : "text-gray-400"}`}>{quarter.q}</span>
                  <h3 className="font-semibold">{quarter.title}</h3>
                  {quarter.status === "complete" && <CheckCircle className="w-4 h-4 text-green-500" />}
                </div>
                <ul className="grid md:grid-cols-2 gap-1 text-sm text-gray-600">
                  {quarter.items.map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      {quarter.status === "complete" ? <Check className="w-3 h-3 text-green-500" /> : <ChevronRight className="w-3 h-3 text-gray-300" />}
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Financials */}
        <section id="financials" ref={(el) => (sectionRefs.current.financials = el)} className="mb-16">
          <div className="border-l-4 border-emerald-500 pl-6 mb-8">
            <h2 className="text-2xl font-bold mb-2">Financial Projections</h2>
            <p className="text-gray-600">3-Year Revenue Model</p>
          </div>
          
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-4 font-semibold">Metric</th>
                  <th className="text-right p-4 font-semibold">Year 1</th>
                  <th className="text-right p-4 font-semibold">Year 2</th>
                  <th className="text-right p-4 font-semibold">Year 3</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr><td className="p-4 font-medium">Paying Providers</td><td className="p-4 text-right">100</td><td className="p-4 text-right">500</td><td className="p-4 text-right">2,000</td></tr>
                <tr><td className="p-4 font-medium">Avg Revenue/Provider</td><td className="p-4 text-right">$25/mo</td><td className="p-4 text-right">$35/mo</td><td className="p-4 text-right">$45/mo</td></tr>
                <tr><td className="p-4 font-medium">MRR</td><td className="p-4 text-right">$2,500</td><td className="p-4 text-right">$17,500</td><td className="p-4 text-right">$90,000</td></tr>
                <tr className="bg-emerald-50"><td className="p-4 font-bold">ARR</td><td className="p-4 text-right font-bold">$30K</td><td className="p-4 text-right font-bold">$210K</td><td className="p-4 text-right font-bold">$1.08M</td></tr>
                <tr><td className="p-4 font-medium">Consumer Users</td><td className="p-4 text-right">1,000</td><td className="p-4 text-right">10,000</td><td className="p-4 text-right">50,000</td></tr>
              </tbody>
            </table>
          </div>
          
          <p className="text-xs text-gray-500 mt-4">
            * Projections assume 5x consumer:provider ratio, 80% gross margins, and conservative growth estimates.
          </p>
        </section>

        {/* Team */}
        <section id="team" ref={(el) => (sectionRefs.current.team = el)} className="mb-16">
          <div className="border-l-4 border-rose-500 pl-6 mb-8">
            <h2 className="text-2xl font-bold mb-2">Team</h2>
            <p className="text-gray-600">Founding team and advisors</p>
          </div>
          
          <div className="bg-gray-50 rounded-xl p-6 text-center">
            <p className="text-gray-500">Team information to be added</p>
            <p className="text-sm text-gray-400 mt-2">Contact hello@phygital.systems for inquiries</p>
          </div>
        </section>

        {/* Appendix */}
        <section id="appendix" ref={(el) => (sectionRefs.current.appendix = el)} className="mb-16">
          <div className="border-l-4 border-gray-400 pl-6 mb-8">
            <h2 className="text-2xl font-bold mb-2">Appendix</h2>
            <p className="text-gray-600">Technical details and supporting information</p>
          </div>
          
          <div className="space-y-3">
            {[
              { id: "tech", title: "Technology Stack", content: (
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div><strong>Frontend:</strong> React, Tailwind CSS, Shadcn/UI</div>
                  <div><strong>Backend:</strong> FastAPI (Python), MongoDB</div>
                  <div><strong>Auth:</strong> Phone OTP, Google OAuth</div>
                  <div><strong>Integrations:</strong> Beehiiv, Notion, Stripe, Acuity (planned)</div>
                  <div><strong>Infrastructure:</strong> Kubernetes, Emergent Platform</div>
                  <div><strong>Hardware:</strong> NFC readers (custom integration)</div>
                </div>
              )},
              { id: "accounts", title: "Account Types", content: (
                <div className="text-sm space-y-2">
                  <p><strong>Consumer:</strong> Free forever, manages preferences and experiences</p>
                  <p><strong>Provider (Standard):</strong> Paid subscription tiers (Free/Solo/Team/Luxe)</p>
                  <p><strong>Provider (Legacy):</strong> Lifetime Luxe access for pilot providers</p>
                  <p><strong>Admin:</strong> Platform management and analytics</p>
                </div>
              )},
              { id: "api", title: "API Endpoints", content: (
                <div className="font-mono text-xs bg-gray-900 text-gray-300 p-4 rounded-lg overflow-x-auto">
                  <pre>{`POST /api/auth/phone-login    - Phone authentication
POST /api/auth/verify-otp     - OTP verification
GET  /api/me                  - Get user profile
PUT  /api/me                  - Update profile
POST /api/experiences         - Create experience
GET  /api/user/inspirations   - Get user's inspo
POST /api/provider/search     - Search providers
POST /api/waitlist/join       - Provider waitlist`}</pre>
                </div>
              )}
            ].map((section) => (
              <div key={section.id} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setExpandedAppendix(expandedAppendix === section.id ? null : section.id)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50"
                >
                  <span className="font-medium">{section.title}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${expandedAppendix === section.id ? "rotate-180" : ""}`} />
                </button>
                {expandedAppendix === section.id && (
                  <div className="p-4 pt-0 border-t border-gray-100">
                    {section.content}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Footer */}
        <div className="border-t border-gray-200 pt-8 text-center">
          <PhygitalIcon className="w-10 h-10 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">hello@phygital.systems</p>
          <p className="text-gray-400 text-xs mt-2">Confidential - Do not distribute</p>
        </div>
      </div>
    </div>
  );
};

export default BusinessPlanPage;
